import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut, getAuth } from 'firebase/auth';
import { useAuth } from '../../auth/AuthProvider';
import { formatOnTimeRoleLabel, getOnTimeLandingPath, getVisibleOnTimeTabs } from '../utils/access';
import { ACTIVE_CONTEXT_EVENT, clearLinkProfileSession, readLinkProfileSession } from '../auth/session';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { getActiveStoreNames } from '../lib/ontimeSettings';
import '../styles/linkSystemRevamp.css';

const FALLBACK_STORES = ['District Six', 'Parow'];


function safeDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function minutesSince(value, now = Date.now()) {
  const date = safeDate(value);
  if (!date) return 0;
  return Math.max(0, Math.floor((now - date.getTime()) / 60000));
}

function formatMinutes(mins) {
  const safe = Math.max(0, Number(mins || 0));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function firstValue(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function orderNumber(order = {}) {
  return firstValue(order.orderNumber, order.raw?.orderNumber, order.id, '000000');
}

function orderStore(order = {}) {
  return firstValue(order.store, order.raw?.storeName, order.raw?.store, 'Unassigned site');
}

function customerName(order = {}) {
  return firstValue(order.customerName, order.raw?.customer?.name, order.raw?.customerName, 'Customer');
}

function isArchivedOrder(order = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const status = String(order.status || raw.status || '').toLowerCase();
  return Boolean(
    order.archived ||
    raw.archived ||
    order.archivedAt ||
    raw.archivedAt ||
    order.reroutedToOrderId ||
    raw.reroutedToOrderId ||
    order.archiveCategory === 'rerouted' ||
    raw.archiveCategory === 'rerouted' ||
    ['archived', 'cancelled', 'delivered'].includes(stage) ||
    ['archived', 'cancelled', 'delivered'].includes(status)
  );
}

function orderHasAdjustment(order = {}) {
  const raw = order.raw || {};
  const flags = order.flags || raw.flags || {};
  const additions = [...(Array.isArray(order.additions) ? order.additions : []), ...(Array.isArray(raw.additions) ? raw.additions : [])];
  const unavailable = [order.unavailableItems, raw.unavailableItems, order.unavailableItemNames, raw.unavailableItemNames].some(Array.isArray);
  return Boolean(order.swapApproved || flags.hasStockIssue || additions.length || unavailable);
}

function orderBalance(order = {}) {
  const total = Number(order.total ?? order.raw?.pricing?.total ?? order.raw?.total ?? 0) || 0;
  const live = Number(order.liveTotal ?? order.raw?.pricing?.liveTotal ?? order.raw?.pricing?.totalToCollect ?? 0) || 0;
  return live && total ? live - total : 0;
}

function timingSource(order = {}) {
  const t = order.timings || {};
  const stage = String(order.stage || '').toLowerCase();
  if (stage === 'packing') return t.packingStartedAt || t.storeAcceptedAt || t.sentToStoreAt || order.updatedAt || order.createdAt;
  if (stage === 'ready_for_driver') return t.readyForDriverAt || t.packedAt || order.updatedAt || order.createdAt;
  if (stage === 'out_for_delivery') return t.outForDeliveryAt || t.collectedAt || t.driverAcceptedAt || order.updatedAt || order.createdAt;
  return t.swapApprovalStartedAt || t.unavailableMarkedAt || t.sentToStoreAt || order.createdAt || order.placedAt;
}

function buildLinkNotifications(orders = [], drivers = [], now = Date.now(), fulfilmentStores = FALLBACK_STORES) {
  const driverRows = Array.isArray(drivers) ? drivers : [];
  const activeOrders = (Array.isArray(orders) ? orders : [])
    .filter((order) => fulfilmentStores.includes(orderStore(order)))
    .filter((order) => !isArchivedOrder(order));
  const notices = [];
  const add = (notice) => notices.push({ ...notice, id: notice.id || `${notice.type}-${notice.orderId || notice.site || notices.length}` });

  activeOrders.forEach((order) => {
    const stage = String(order.stage || '').toLowerCase();
    const mins = minutesSince(timingSource(order), now);
    const base = {
      orderId: order.id,
      orderNumber: orderNumber(order),
      site: orderStore(order),
      customer: customerName(order),
      age: formatMinutes(mins),
    };

    if (orderHasAdjustment(order) && !order.swapApproved && mins >= 5) {
      add({ ...base, type: 'Approval delay', severity: 'critical', body: `${base.site} has an adjustment waiting ${formatMinutes(mins)}.`, action: 'Open approval', score: 4000 + mins, focus: 'approval' });
    }
    if (['new', 'waiting', 'awaiting_store'].includes(stage) && mins >= 10) {
      add({ ...base, type: 'Waiting delay', severity: 'critical', body: `${base.site} has not started this order for ${formatMinutes(mins)}.`, action: 'Message site', score: 3600 + mins, focus: 'chat' });
    }
    if (stage === 'packing' && mins >= 12) {
      add({ ...base, type: 'Packing stalled', severity: mins >= 20 ? 'critical' : 'delayed', body: `Packing has been active for ${formatMinutes(mins)}.`, action: 'Open order', score: (mins >= 20 ? 3500 : 2500) + mins, focus: 'order' });
    }
    if (stage === 'ready_for_driver' && mins >= 8) {
      add({ ...base, type: 'Driver needed', severity: 'critical', body: `Ready order is waiting ${formatMinutes(mins)} for collection.`, action: 'Open driver panel', score: 3400 + mins, focus: 'driver' });
    }
    if (stage === 'out_for_delivery' && mins >= 25) {
      add({ ...base, type: 'Delivery delay', severity: mins >= 50 ? 'critical' : 'delayed', body: `Driver has been on route for ${formatMinutes(mins)}.`, action: 'Open timeline', score: (mins >= 50 ? 3400 : 2400) + mins, focus: 'timeline' });
    }

    const assignedDriver = firstValue(order.assignedDriverName, order.assignedDriver, order.assignment?.driverName, order.assignment?.driverId);
    if (stage === 'out_for_delivery' && assignedDriver) {
      const driver = driverRows.find((row) => [row.name, row.fullName, row.displayName, row.id, row.email].map((v) => String(v || '').toLowerCase()).includes(String(assignedDriver).toLowerCase()));
      const status = String(driver?.status || driver?.state || '').toLowerCase();
      const online = driver ? (driver.online === true || ['online', 'available', 'busy', 'on_run'].includes(status)) : true;
      if (!online) add({ ...base, type: 'Driver offline', severity: 'critical', body: `${assignedDriver} appears offline during an active run.`, action: 'Open driver panel', score: 3900 + mins, focus: 'driver' });
    }

    const balance = orderBalance(order);
    if (Math.abs(balance) > 0.01) {
      add({ ...base, type: balance > 0 ? 'Customer owes' : 'Refund due', severity: balance > 0 ? 'operational' : 'critical', body: `${balance > 0 ? 'Customer owes' : 'Refund due'} R ${Math.abs(balance).toFixed(0)}.`, action: 'Review totals', score: (balance > 0 ? 1800 : 3200) + mins, focus: 'totals' });
    }
  });

  const oneSignalPerOrder = [];
  const seenOrders = new Set();
  notices
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .forEach((notice) => {
      const key = notice.orderId || notice.id;
      if (seenOrders.has(key)) return;
      seenOrders.add(key);
      oneSignalPerOrder.push(notice);
    });

  return oneSignalPerOrder.slice(0, 12);
}

const notificationTone = {
  critical: { dot: '#ff6b99', bg: 'rgba(255,75,130,0.14)', border: 'rgba(255,75,130,0.35)' },
  delayed: { dot: '#ffd166', bg: 'rgba(255,209,102,0.12)', border: 'rgba(255,209,102,0.32)' },
  operational: { dot: '#5ee7ff', bg: 'rgba(94,231,255,0.11)', border: 'rgba(94,231,255,0.28)' },
};

const TAB_ICONS = {
  Overview: '▦',
  'Control Tower': '▣',
  'Online Intake': '◈',
  Products: '⬡',
  'Store Queue': '⌘',
  'Driver View': '▱',
  'Control Map': '⌖',
  Orders: '▥',
  Reports: '▤',
  Customers: '◎',
  Archives: '⌁',
  Settings: '⚙',
  Profiles: '◌',
  'My View': '◍',
};

export default function OnTimeShell() {
  const nav = useNavigate();
  const location = useLocation();
  const linkControlBase = location.pathname.startsWith('/control') ? '/control' : '/link';
  const toControlPath = useCallback((path = '/link') => String(path || '/link').replace(/^\/link(?=\/|$)/, linkControlBase), [linkControlBase]);
  const { user, claims, refreshClaims } = useAuth();
  const [profileSession, setProfileSession] = useState(() => readLinkProfileSession());
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [commandSearch, setCommandSearch] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationPulse, setNotificationPulse] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const notificationWrapRef = useRef(null);
  const { orders = [], drivers = [], settings } = useOnTimeLiveData();
  const [notificationNow, setNotificationNow] = useState(() => Date.now());
  const fulfilmentStores = useMemo(() => {
    const names = getActiveStoreNames(settings || {});
    return names.length ? names : FALLBACK_STORES;
  }, [settings]);
  const activeClaims = profileSession?.claims || claims;
  const landing = getOnTimeLandingPath(activeClaims);
  const tabs = getVisibleOnTimeTabs(activeClaims);
  const displayName = String(activeClaims?.displayName || `${activeClaims?.fullName || ''} ${activeClaims?.surname || ''}`.trim() || profileSession?.profileName || user?.displayName || user?.email || 'Signed in user');
  const roleLabel = formatOnTimeRoleLabel(activeClaims?.onTimeRole || activeClaims?.role || 'user');
  const initials = useMemo(() => displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'LK', [displayName]);


  const navGroups = useMemo(() => {
    const mapTab = (tab) => tab ? { ...tab, to: toControlPath(tab.to || '/link') } : tab;
    const byLabel = Object.fromEntries(tabs.map((tab) => [tab.label, mapTab(tab)]));
    const overviewTab = byLabel['Overview'] || (byLabel['Control Tower'] ? { ...byLabel['Control Tower'], key: 'overview', to: linkControlBase, label: 'Overview', iconKey: 'Overview', end: true } : { key: 'overview', label: 'Overview', to: linkControlBase, iconKey: 'Overview', end: true });
    const controlMapTab = byLabel['Control Map'] || { key: 'control-map', label: 'Control Map', to: `${linkControlBase}/map`, iconKey: 'Control Map' };
    const controlTowerTab = byLabel['Control Tower'] ? { ...byLabel['Control Tower'], to: `${linkControlBase}/tower`, label: 'Control Tower', iconKey: 'Control Tower', end: false } : { key: 'control-tower', label: 'Control Tower', to: `${linkControlBase}/tower`, iconKey: 'Control Tower' };
    return [
      { title: '', items: [overviewTab].filter(Boolean) },
      { title: 'Operations', items: [
        controlMapTab,
        controlTowerTab,
        (byLabel['Orders'] || { key: 'orders', label: 'Orders', to: `${linkControlBase}/orders`, iconKey: 'Orders' }),
        ...['Online Intake', 'Products', 'Store Queue', 'Driver View', 'Customers', 'Archives'].map((label) => byLabel[label]).filter(Boolean),
      ].filter(Boolean) },
      { title: 'Intelligence', items: ['Reports'].map((label) => byLabel[label]).filter(Boolean) },
      { title: 'Communication', items: [] },
      { title: 'Settings', items: ['Settings', 'Profiles', 'My View'].map((label) => byLabel[label]).filter(Boolean) },
    ].filter((group) => group.items.length || group.title === 'Communication');
  }, [linkControlBase, tabs, toControlPath]);

  const firstName = displayName.split(/\s+/).filter(Boolean)[0] || 'there';
  const currentDateLabel = new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit' });
  const currentTimeLabel = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const notifications = useMemo(() => buildLinkNotifications(orders, drivers, notificationNow, fulfilmentStores), [drivers, notificationNow, orders, fulfilmentStores]);
  const criticalNotificationCount = notifications.filter((item) => item.severity === 'critical').length;

  useEffect(() => {
    const tick = window.setInterval(() => setNotificationNow(Date.now()), 30000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!notifications.length) return;
    setNotificationPulse(true);
    const timer = window.setTimeout(() => setNotificationPulse(false), 1400);
    return () => window.clearTimeout(timer);
  }, [notifications.length, criticalNotificationCount]);

  const openNotification = (notice) => {
    setNotificationsOpen(false);
    if (!notice?.orderId) return;
    const detail = { orderId: notice.orderId, focus: notice.focus || 'timeline' };
    if (location.pathname !== linkControlBase) {
      try { window.sessionStorage.setItem('link-pending-open-order', JSON.stringify(detail)); } catch (error) { console.error('[Link] pending order store failed', error); }
      nav(linkControlBase);
      return;
    }
    window.dispatchEvent(new CustomEvent('link-open-order', { detail }));
  };


  useEffect(() => {
    window.dispatchEvent(new CustomEvent('link-command-search', { detail: { query: commandSearch } }));
  }, [commandSearch]);


  useEffect(() => {
    const clearSearch = () => setCommandSearch('');
    window.addEventListener('link-command-search-clear', clearSearch);
    return () => window.removeEventListener('link-command-search-clear', clearSearch);
  }, []);

  useEffect(() => {
    const closeFloatingPanels = (event) => {
      const target = event.target;
      const insideSearchInput = searchWrapRef.current?.contains?.(target);
      const insideSearchPanel = target?.closest?.('.link-command-search-panel');
      const insideNotification = notificationWrapRef.current?.contains?.(target);

      if (commandSearch && !insideSearchInput && !insideSearchPanel) {
        setCommandSearch('');
      }
      if (notificationsOpen && !insideNotification) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeFloatingPanels);
    document.addEventListener('touchstart', closeFloatingPanels, { passive: true });
    return () => {
      document.removeEventListener('mousedown', closeFloatingPanels);
      document.removeEventListener('touchstart', closeFloatingPanels);
    };
  }, [commandSearch, notificationsOpen]);

  useEffect(() => {
    const focusSearch = (event) => {
      if ((event.ctrlKey || event.metaKey) && String(event.key || '').toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus?.();
      }
      if (String(event.key || '') === 'Escape') setCommandSearch('');
    };
    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  useEffect(() => {
    const syncProfileSession = () => setProfileSession(readLinkProfileSession());
    window.addEventListener(ACTIVE_CONTEXT_EVENT, syncProfileSession);
    window.addEventListener('storage', syncProfileSession);
    syncProfileSession();
    return () => {
      window.removeEventListener(ACTIVE_CONTEXT_EVENT, syncProfileSession);
      window.removeEventListener('storage', syncProfileSession);
    };
  }, []);

  async function switchUser() {
    clearLinkProfileSession();
    await refreshClaims?.({ force: false });
    nav(linkControlBase, { replace: true, state: { forceLinkAccessLayer: true } });
  }

  async function logout() {
    clearLinkProfileSession();
    await signOut(getAuth());
    nav('/login', { replace: true, state: { preferredApp: 'ontime' } });
  }

  return (
    <div className="link-system-shell min-h-screen text-white">
      <aside
        className={`link-side-rail link-side-rail-collapsible ${sidebarExpanded ? 'link-side-rail-expanded' : 'link-side-rail-collapsed'}`}
      >
        <button
          type="button"
          className="link-sidebar-toggle"
          onClick={() => setSidebarExpanded((open) => !open)}
          aria-label={sidebarExpanded ? 'Collapse Link sidebar' : 'Expand Link sidebar'}
        >
          {sidebarExpanded ? '‹' : '☰'}
        </button>

        <button type="button" onClick={() => nav(linkControlBase, { replace: true })} className="link-logo-word" aria-label="Link home">
          <span className="link-brand-text">Link</span><i>.</i>
        </button>

        <nav className="link-side-nav link-side-nav-grouped" aria-label="Link navigation">
          {navGroups.map((group) => (
            <div key={group.title || 'overview'} className="link-nav-group">
              {group.title ? <div className="link-nav-group-title">{group.title}</div> : null}
              {group.items.map((tab) => tab.placeholder ? (
                <button key={`placeholder-${tab.label}`} type="button" className="link-side-pill link-side-pill-placeholder" title={`${tab.label} is moving here next`}>
                  <span className="link-side-icon">{TAB_ICONS[tab.iconKey || tab.label] || '•'}</span>
                  <span className="link-side-label">{tab.label}</span>
                  <span className="link-side-chevron">›</span>
                </button>
              ) : (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) => `link-side-pill ${isActive ? 'link-side-pill-active' : ''}`}
                  title={tab.label}
                >
                  <span className="link-side-icon">{TAB_ICONS[tab.iconKey || tab.label] || '•'}</span>
                  <span className="link-side-label">{tab.label}</span>
                  <span className="link-side-chevron">›</span>
                </NavLink>
              ))}
              {group.title === 'Communication' ? (
                <>
                  <button type="button" className="link-side-pill" title="Alerts"><span className="link-side-icon">△</span><span className="link-side-label">Alerts</span>{notifications.length ? <b className="link-side-badge">{notifications.length}</b> : null}</button>
                  <button type="button" className="link-side-pill" title="Broadcast"><span className="link-side-icon">⌁</span><span className="link-side-label">Broadcast</span><span className="link-side-chevron">›</span></button>
                  <button type="button" className="link-side-pill" title="Messages"><span className="link-side-icon">▱</span><span className="link-side-label">Messages</span></button>
                </>
              ) : null}
            </div>
          ))}
        </nav>

        <div className="link-sidebar-account-actions">
          <button type="button" onClick={switchUser} className="link-action-button link-action-cyan">⇄ Switch user</button>
          <button type="button" onClick={() => setConfirmLogout(true)} className="link-action-button link-action-rose">↪ Logout</button>
        </div>

        <div className="link-status-card">
          <div className="text-sm font-semibold text-white">MatrixOS</div>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-300"><span className="link-live-dot" /> Operate with Clarity.</div>
        </div>
      </aside>

      <div className="link-main-frame">
        <header className="link-topbar">
          <div className="link-top-greeting">
            <h1 className="link-page-title">Good morning, {firstName} 👋</h1>
            <p className="link-page-subtitle">Here's what's happening across your network.</p>
          </div>

          <div className="link-top-meta" aria-label="Live network context"><span className="link-live-status">● Live</span><span>⌖ Cape Town</span><span>▣ {currentDateLabel}</span><span>{currentTimeLabel}</span></div>

          <label ref={searchWrapRef} className="link-top-search link-top-search-active">
            <input
              ref={searchInputRef}
              value={commandSearch}
              onChange={(event) => setCommandSearch(event.target.value)}
              placeholder="Search orders, drivers, customers..."
              aria-label="Search Link operations"
            />
            {commandSearch ? <button type="button" onClick={() => setCommandSearch('')} aria-label="Clear search">×</button> : null}
            <kbd>Ctrl</kbd><kbd>K</kbd>
          </label>

          <div ref={notificationWrapRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setNotificationsOpen((open) => !open)}
              title="Operational notifications"
              aria-label="Operational notifications"
              style={{
                width: 44,
                height: 44,
                borderRadius: 16,
                border: criticalNotificationCount ? '1px solid rgba(255,107,153,0.55)' : '1px solid rgba(94,231,255,0.22)',
                background: criticalNotificationCount ? 'rgba(255,75,130,0.14)' : 'rgba(8, 20, 38, 0.82)',
                color: '#e9fbff',
                boxShadow: notificationPulse ? '0 0 0 6px rgba(255,107,153,0.12), 0 0 28px rgba(94,231,255,0.18)' : '0 0 20px rgba(94,231,255,0.08)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 900,
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              🔔
              {notifications.length ? (
                <span style={{
                  position: 'absolute',
                  top: -7,
                  right: -7,
                  minWidth: 22,
                  height: 22,
                  borderRadius: 999,
                  padding: '0 6px',
                  background: criticalNotificationCount ? '#ff4f87' : '#1b8dff',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.28)',
                  fontSize: 11,
                  display: 'grid',
                  placeItems: 'center',
                }}>{notifications.length}</span>
              ) : null}
            </button>

            {notificationsOpen ? (
              <div style={{
                position: 'absolute',
                top: 54,
                right: 0,
                width: 390,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 'min(640px, calc(100vh - 150px))',
                overflowY: 'auto',
                borderRadius: 22,
                padding: 16,
                background: 'linear-gradient(180deg, rgba(7,17,34,0.98), rgba(8,19,36,0.94))',
                border: '1px solid rgba(94,231,255,0.28)',
                boxShadow: '0 26px 80px rgba(0,0,0,0.58), 0 0 36px rgba(94,231,255,0.12)',
                zIndex: 80,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ color: '#67e8f9', fontSize: 11, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase' }}>Attention feed</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>{notifications.length ? `${notifications.length} live signal${notifications.length === 1 ? '' : 's'}` : 'All clear'}</div>
                  </div>
                  <button type="button" onClick={() => setNotificationsOpen(false)} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999, background: 'rgba(255,255,255,0.04)', color: '#dffaff', padding: '7px 10px', cursor: 'pointer' }}>Close</button>
                </div>

                {notifications.length ? notifications.map((notice) => {
                  const tone = notificationTone[notice.severity] || notificationTone.operational;
                  return (
                    <button key={notice.id} type="button" onClick={() => openNotification(notice)} style={{
                      width: '100%',
                      border: `1px solid ${tone.border}`,
                      background: tone.bg,
                      borderRadius: 17,
                      padding: 13,
                      color: '#fff',
                      textAlign: 'left',
                      display: 'grid',
                      gap: 8,
                      marginTop: 10,
                      cursor: 'pointer',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <strong style={{ fontSize: 14 }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: tone.dot, marginRight: 8 }} />{notice.type}</strong>
                        <span style={{ borderRadius: 999, padding: '4px 8px', background: 'rgba(255,255,255,0.07)', color: '#dffaff', fontSize: 11, fontWeight: 900, textTransform: 'capitalize' }}>{notice.severity}</span>
                      </div>
                      <div style={{ color: '#c8d8ef', fontSize: 13 }}>{notice.body}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: '#8defff', fontSize: 12, fontWeight: 900 }}>
                        <span>#{notice.orderNumber} · {notice.site}</span>
                        <span>{notice.action}</span>
                      </div>
                    </button>
                  );
                }) : (
                  <div style={{ borderRadius: 17, padding: 16, background: 'rgba(20,34,55,0.75)', color: '#cbd5e1' }}>
                    No operational notifications right now. Clean flow.
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="link-user-block">
            <div className="link-avatar-ring">{initials}</div>
            <div>
              <div className="font-semibold text-white">{displayName}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-cyan-200/60">{roleLabel}</div>
            </div>
          </div>


        </header>

        <main className="link-page-wrap">
          <Outlet />
        </main>

        <footer className="link-footer-bar">
          <div className="link-footer-logo">Link<span>.</span></div>
          <div>© 2026 MatrixOS. All systems linked.</div>
          <div className="ml-auto hidden gap-8 lg:flex"><span>● Secure connection</span><span>● System operational</span><span>⌖ Cape Town, SA</span></div>
        </footer>
      </div>

      {confirmLogout ? (
        <div className="link-modal-backdrop" role="presentation">
          <div className="link-modal-card" role="dialog" aria-modal="true" aria-labelledby="link-logout-title">
            <button type="button" className="link-modal-close" onClick={() => setConfirmLogout(false)}>×</button>
            <div className="link-modal-icon">↪</div>
            <h2 id="link-logout-title" className="text-2xl font-black text-white">Logout of Link?</h2>
            <p className="mt-2 text-sm text-slate-300">This will sign out of MatrixOS on this browser.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button type="button" className="link-modal-secondary" onClick={() => setConfirmLogout(false)}>Cancel</button>
              <button type="button" className="link-modal-danger" onClick={logout}>Logout</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
